// src/pages/TicketDesignTemplate/index.tsx
import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import BreadCrumb from 'Components/Common/BreadCrumb';
import { fetchTicketDesignTemplates } from 'slices/ticketDesignTemplate/thunk';
import {
  setPage,
  setPageSize,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
  setSelectedTemplate,
} from 'slices/ticketDesignTemplate/reducer';
import TicketDesignTemplateList from './TicketDesignTemplateList';
import CreateTicketDesignTemplateModal from './modals/CreateTicketDesignTemplateModal';
import UpdateTicketDesignTemplateModal from './modals/UpdateTicketDesignTemplateModal';
import FilePreviewModal from './modals/FilePreviewModal';
import DeleteConfirmModal from './modals/DeleteConfirmModal';
import ApproveConfirmModal from './modals/ApproveConfirmModal';
import { TicketDesignTemplate } from './types';
import {
  deleteTicketDesignTemplate,
  approveTicketDesignTemplate,
  unlockTicketDesignTemplate,
} from 'slices/ticketDesignTemplate/thunk';
import { toast } from 'react-toastify';
import { useBrandingTitle } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

const TicketDesignTemplatePage = () => {
  useBrandingTitle(st('quan_ly_mau_thiet_ke_ve_text'));

  const dispatch = useDispatch();
  const {
    templates,
    selectedTemplate,
    pagination,
    filters,
    loading,
    modalState,
  } = useSelector((state: any) => state.TicketDesignTemplate);

  const [deleteConfirm, setDeleteConfirm] = useState<TicketDesignTemplate | null>(null);
  const [approveConfirm, setApproveConfirm] = useState<TicketDesignTemplate | null>(null);

  // Load danh sách templates khi component mount hoặc khi filters/pagination thay đổi
  useEffect(() => {
    loadTemplates();
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const loadTemplates = () => {
    const params = {
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
      search: filters.searchTerm,
      status: filters.status === 'All' || !filters.status ? undefined : Number(filters.status),
      designUnit: filters.designUnit === 'All' ? undefined : filters.designUnit,
      issuancePeriod: filters.issuancePeriod || undefined,
    };
    dispatch(fetchTicketDesignTemplates(params) as any);
  };

  // Handlers cho các actions
  const handleCreate = () => {
    dispatch(openModal('create'));
  };

  const handleEdit = (template: TicketDesignTemplate) => {
    dispatch(setSelectedTemplate(template));
    dispatch(openModal('edit'));
  };

  const handlePreview = (template: TicketDesignTemplate) => {
    dispatch(setSelectedTemplate(template));
    dispatch(openModal('preview'));
  };

  const handleApprove = (template: TicketDesignTemplate) => {
    setApproveConfirm(template);
  };

  const handleDelete = (template: TicketDesignTemplate) => {
    setDeleteConfirm(template);
  };

  const handleUnlock = async (template: TicketDesignTemplate) => {
    if (!template.id) return;

    try {
      const result = await dispatch(unlockTicketDesignTemplate(template.id) as any);

      if (unlockTicketDesignTemplate.fulfilled.match(result)) {
        toast.success(st('mo_khoa_mau_thiet_ke_thanh_message'));
        loadTemplates();
      } else {
        toast.error(
          typeof result.payload === 'string' ? result.payload : st('co_loi_xay_ra_khi_mo_message')
        );
      }
    } catch (error) {
      toast.error(st('co_loi_xay_ra_khi_mo_message'));
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(setPageSize(pageSize));
  };

  const handleFilterChange = (filterName: string, value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleCloseModal = (modalName: string) => {
    dispatch(closeModal(modalName as any));
    dispatch(setSelectedTemplate(null));
  };

    const handleSuccess = () => {
    loadTemplates();
  };

  // Xử lý xác nhận xóa
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm?.id) return;

    try {
      const result = await dispatch(deleteTicketDesignTemplate(deleteConfirm.id) as any);
      
      if (deleteTicketDesignTemplate.fulfilled.match(result)) {
        toast.success(st('khoa_mau_thiet_ke_thanh_cong_message'));
        setDeleteConfirm(null);
        loadTemplates();
      } else {
        toast.error(
          typeof result.payload === 'string' ? result.payload : st('co_loi_xay_ra_khi_khoa_message')
        );
      }
    } catch (error) {
      toast.error(st('co_loi_xay_ra_khi_khoa_message'));
    }
  };

  // Xử lý xác nhận phê duyệt
  const handleApproveConfirm = async () => {
    if (!approveConfirm?.id) return;

    try {
      const result = await dispatch(approveTicketDesignTemplate(approveConfirm.id) as any);
      
      if (approveTicketDesignTemplate.fulfilled.match(result)) {
        toast.success(st('phe_duyet_mau_thiet_ke_thanh_message'));
        setApproveConfirm(null);
        loadTemplates();
      } else {
        toast.error(
          typeof result.payload === 'string' ? result.payload : st('co_loi_xay_ra_khi_phe_message')
        );
      }
    } catch (error) {
      toast.error(st('co_loi_xay_ra_khi_phe_message'));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('quan_ly_mau_thiet_ke_ve_text')} pageTitle={st('he_thong_text')} />

          <Row>
            <Col lg={12}>
              <TicketDesignTemplateList
                templates={templates}
                pagination={pagination}
                filters={filters}
                loading={loading}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onCreate={handleCreate}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onApprove={handleApprove}
                onDelete={handleDelete}
                onUnlock={handleUnlock}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create Modal */}
      {modalState.create && (
        <CreateTicketDesignTemplateModal
          isOpen={modalState.create}
          onClose={() => handleCloseModal('create')}
          onSuccess={handleSuccess}
        />
      )}

      {/* Update Modal */}
      {modalState.edit && selectedTemplate && (
        <UpdateTicketDesignTemplateModal
          isOpen={modalState.edit}
          template={selectedTemplate}
          onClose={() => handleCloseModal('edit')}
          onSuccess={handleSuccess}
        />
      )}

            {/* File Preview Modal */}
      {modalState.preview && selectedTemplate && (
        <FilePreviewModal
          isOpen={modalState.preview}
          template={selectedTemplate}
          onClose={() => handleCloseModal('preview')}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          isOpen={!!deleteConfirm}
          template={deleteConfirm}
          loading={loading}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Approve Confirm Modal */}
      {approveConfirm && (
        <ApproveConfirmModal
          isOpen={!!approveConfirm}
          template={approveConfirm}
          loading={loading}
          onClose={() => setApproveConfirm(null)}
          onConfirm={handleApproveConfirm}
        />
      )}
    </React.Fragment>
  );
};

export default TicketDesignTemplatePage;
